import { endOfWeek, startOfWeek } from 'date-fns';
import { sortObjectByTimestampSubField } from '../../components/helpers/sortHelpers';
import { SiteLog } from '../../constants/SiteLogs';
import { Firestore, FirestoreDataConverter, FirestoreError } from '../firebase';

const SITE_LOGS_COLLECTION = 'siteLogsV2';

const siteLogConverter: FirestoreDataConverter<SiteLog> = {
	toFirestore: (model) => model,
	fromFirestore: (snapshot, _) => snapshot.data() as SiteLog,
};

/** Returns the first signIn SiteLog found for workers current site or null if worker hasn't signed in within provided period */
const getFirstWorkerSiteSignInSiteLogForPeriod = async (
	workerID: string,
	siteID: string,
	startDate: Date,
	endDate: Date,
): Promise<SiteLog | null> => {
	const inDocs = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('worker.id', '==', workerID)
		.where('site.id', '==', siteID)
		.where('in.dateTime', '>=', startDate)
		.where('in.dateTime', '<=', endDate)
		.where('out', '==', null)
		.where('autoSignedOut', '==', false)
		.orderBy('in.dateTime', 'asc')
		.withConverter(siteLogConverter)
		.limit(1)
		.get();

	const inLog = inDocs.size === 1 ? inDocs.docs[0].data() : null;

	const inOutDocs = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('worker.id', '==', workerID)
		.where('site.id', '==', siteID)
		.where('out.dateTime', '>=', startDate)
		.where('out.dateTime', '<=', endDate)
		.withConverter(siteLogConverter)
		.get();

	if (!inOutDocs.empty) {
		const inOutLogs = inOutDocs.docs.map((doc) => doc.data());

		const sortedInOutDocs = sortObjectByTimestampSubField(
			inOutLogs,
			'in',
			'dateTime',
		);

		const firstInOutLog = sortedInOutDocs[0];

		if (firstInOutLog && !inLog) {
			return firstInOutLog;
		} else if (
			firstInOutLog &&
			inLog &&
			firstInOutLog.in.dateTime < inLog.in.dateTime
		) {
			return firstInOutLog;
		} else {
			return inLog;
		}
	}

	return inLog;
};

const getUserSiteLogsByInDateForWeek = async (
	workerID: string,
	siteID: string,
	weekEnding: Date,
): Promise<SiteLog[]> => {
	const siteLogDocs = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('worker.id', '==', workerID)
		.where('site.id', '==', siteID)
		.where('in.dateTime', '>=', startOfWeek(weekEnding))
		.where('in.dateTime', '<=', endOfWeek(weekEnding))
		.where('out', '==', null)
		.withConverter(siteLogConverter)
		.get();

	return siteLogDocs.docs.map((doc) => doc.data());
};

const getSiteLogsByCompanyDateRange = async (
	companyID: string,
	startDate: Date,
	endDate: Date,
): Promise<SiteLog[]> => {
	const siteLogsWithOut = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('company.id', '==', companyID)
		.where('out.dateTime', '>=', startDate)
		.where('out.dateTime', '<=', endDate)
		.withConverter(siteLogConverter)
		.get();

	const inOnlySiteLogs = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('company.id', '==', companyID)
		.where('in.dateTime', '>=', startDate)
		.where('in.dateTime', '<=', endDate)
		.where('out', '==', null)
		.withConverter(siteLogConverter)
		.get();

	const inSiteLogsData = inOnlySiteLogs.docs.map((doc) => doc.data());
	const outSiteLogsData = siteLogsWithOut.docs.map((doc) => doc.data());

	const sortedLogs = sortObjectByTimestampSubField(
		[...inSiteLogsData, ...outSiteLogsData],
		'in',
		'dateTime',
	);

	return sortedLogs;
};

const getSiteLogsBySiteDateRange = async (
	siteID: string,
	startDate: Date,
	endDate: Date,
): Promise<SiteLog[]> => {
	const siteLogsWithOut = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('site.id', '==', siteID)
		.where('out.dateTime', '>=', startDate)
		.where('out.dateTime', '<=', endDate)
		.withConverter(siteLogConverter)
		.get();

	const inOnlySiteLogs = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('site.id', '==', siteID)
		.where('in.dateTime', '>=', startDate)
		.where('in.dateTime', '<=', endDate)
		.where('out', '==', null)
		.withConverter(siteLogConverter)
		.get();

	const inSiteLogsData = inOnlySiteLogs.docs.map((doc) => doc.data());
	const outSiteLogsData = siteLogsWithOut.docs.map((doc) => doc.data());

	const sortedLogs = sortObjectByTimestampSubField(
		[...inSiteLogsData, ...outSiteLogsData],
		'in',
		'dateTime',
	);

	return sortedLogs;
};

const getSiteLogsBySiteCompanyDateRange = async (
	siteCompanyID: string,
	startDate: Date,
	endDate: Date,
): Promise<SiteLog[]> => {
	const siteLogsWithOut = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('site.companyID', '==', siteCompanyID)
		.where('out.dateTime', '>=', startDate)
		.where('out.dateTime', '<=', endDate)
		.withConverter(siteLogConverter)
		.get();

	const inOnlySiteLogs = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('site.companyID', '==', siteCompanyID)
		.where('in.dateTime', '>=', startDate)
		.where('in.dateTime', '<=', endDate)
		.where('out', '==', null)
		.withConverter(siteLogConverter)
		.get();

	const inSiteLogsData = inOnlySiteLogs.docs.map((doc) => doc.data());
	const outSiteLogsData = siteLogsWithOut.docs.map((doc) => doc.data());

	const sortedLogs = sortObjectByTimestampSubField(
		[...inSiteLogsData, ...outSiteLogsData],
		'in',
		'dateTime',
	);

	return sortedLogs;
};

const getInSiteLogsBySiteDateRange = async (
	siteID: string,
	startDate: Date,
	endDate: Date,
): Promise<SiteLog[]> => {
	const inOnlySiteLogs = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('site.id', '==', siteID)
		.where('in.dateTime', '>=', startDate)
		.where('in.dateTime', '<=', endDate)
		.where('out', '==', null)
		.where('autoSignedOut', '==', false)
		.withConverter(siteLogConverter)
		.get();

	return inOnlySiteLogs.docs
		.filter((doc) => doc.exists)
		.map((doc) => doc.data());
};

const getInSiteLogsBySiteContractedToDateRange = async (
	siteID: string,
	contractedToID: string,
	startDate: Date,
	endDate: Date,
): Promise<SiteLog[]> => {
	const inOnlySiteLogs = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('site.id', '==', siteID)
		.where('worker.contractedTo.id', '==', contractedToID)
		.where('in.dateTime', '>=', startDate)
		.where('in.dateTime', '<=', endDate)
		.where('out', '==', null)
		.where('autoSignedOut', '==', false)
		.withConverter(siteLogConverter)
		.get();

	return inOnlySiteLogs.docs
		.filter((doc) => doc.exists)
		.map((doc) => doc.data());
};

const getInSiteLogsBySiteCompanyDateRange = async (
	siteCompanyID: string,
	startDate: Date,
	endDate: Date,
): Promise<SiteLog[]> => {
	const inOnlySiteLogs = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('site.companyID', '==', siteCompanyID)
		.where('in.dateTime', '>=', startDate)
		.where('in.dateTime', '<=', endDate)
		.where('out', '==', null)
		.where('autoSignedOut', '==', false)
		.withConverter(siteLogConverter)
		.get();

	return inOnlySiteLogs.docs
		.filter((doc) => doc.exists)
		.map((doc) => doc.data());
};

const getInSiteLogsByCompanyDateRange = async (
	companyID: string,
	startDate: Date,
	endDate: Date,
): Promise<SiteLog[]> => {
	const inOnlySiteLogs = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('company.id', '==', companyID)
		.where('in.dateTime', '>=', startDate)
		.where('in.dateTime', '<=', endDate)
		.where('out', '==', null)
		.where('autoSignedOut', '==', false)
		.withConverter(siteLogConverter)
		.get();

	return inOnlySiteLogs.docs
		.filter((doc) => doc.exists)
		.map((doc) => doc.data());
};

const getInSiteLogsByContractedToDateRange = async (
	contractedToID: string,
	startDate: Date,
	endDate: Date,
): Promise<SiteLog[]> => {
	const inOnlySiteLogs = await Firestore.collection(SITE_LOGS_COLLECTION)
		.where('worker.contractedTo.id', '==', contractedToID)
		.where('in.dateTime', '>=', startDate)
		.where('in.dateTime', '<=', endDate)
		.where('out', '==', null)
		.where('autoSignedOut', '==', false)
		.withConverter(siteLogConverter)
		.get();

	return inOnlySiteLogs.docs
		.filter((doc) => doc.exists)
		.map((doc) => doc.data());
};

const userSiteLogsForWeekSubscription = (
	workerID: string,
	siteID: string,
	weekEnding: Date,
	onNext: (siteLogs: Record<string, SiteLog>) => void,
	onError?: (error: FirestoreError) => void,
): (() => void) => {
	return Firestore.collection(SITE_LOGS_COLLECTION)
		.where('worker.id', '==', workerID)
		.where('site.id', '==', siteID)
		.where('out.dateTime', '>=', startOfWeek(weekEnding))
		.where('out.dateTime', '<=', endOfWeek(weekEnding))
		.withConverter(siteLogConverter)
		.onSnapshot(async (snapshot) => {
			const inLogs = await getUserSiteLogsByInDateForWeek(
				workerID,
				siteID,
				weekEnding,
			);

			const logs = snapshot.docs.reduce<Record<string, SiteLog>>(
				(prev, current) => {
					const data = current.data();

					prev[current.id] = data;

					return prev;
				},
				{},
			);

			inLogs.forEach((inLog) => {
				if (!logs[inLog.id]) {
					logs[inLog.id] = inLog;
				}
			});

			onNext(logs);
		}, onError);
};

const siteLogsBySiteDateRangeSubscription = (
	siteID: SiteLog['site']['id'],
	startDate: Date,
	endDate: Date,
	onNext: (siteLogs: Record<string, SiteLog>) => void,
	onError?: (error: FirestoreError) => void,
): (() => void) => {
	return Firestore.collection(SITE_LOGS_COLLECTION)
		.where('site.id', '==', siteID)
		.where('out.dateTime', '>=', startDate)
		.where('out.dateTime', '<=', endDate)
		.withConverter(siteLogConverter)
		.onSnapshot(async (snapshot) => {
			const inLogs = await getInSiteLogsBySiteDateRange(
				siteID,
				startDate,
				endDate,
			);

			const logs = snapshot.docs
				.filter((doc) => doc.exists)
				.reduce<Record<string, SiteLog>>((prev, current) => {
					const data = current.data();

					prev[current.id] = data;

					return prev;
				}, {});

			inLogs.forEach((inLog) => {
				if (!logs[inLog.id]) {
					logs[inLog.id] = inLog;
				}
			});

			onNext(logs);
		}, onError);
};

const siteLogsBySiteContractedToDateRangeSubscription = (
	siteID: SiteLog['site']['id'],
	contractedToID: NonNullable<SiteLog['worker']['contractedTo']>['id'],
	startDate: Date,
	endDate: Date,
	onNext: (siteLogs: SiteLog[]) => void,
	onError?: (error: FirestoreError) => void,
): (() => void) => {
	return Firestore.collection(SITE_LOGS_COLLECTION)
		.where('site.id', '==', siteID)
		.where('worker.contractedTo.id', '==', contractedToID)
		.where('out.dateTime', '>=', startDate)
		.where('out.dateTime', '<=', endDate)
		.withConverter(siteLogConverter)
		.onSnapshot(async (querySnapshot) => {
			const inLogs = await getInSiteLogsBySiteContractedToDateRange(
				siteID,
				contractedToID,
				startDate,
				endDate,
			);

			const logs = querySnapshot.docs
				.filter((doc) => doc.exists)
				.reduce<Record<string, SiteLog>>((prev, current) => {
					const data = current.data();

					prev[current.id] = data;

					return prev;
				}, {});

			inLogs.forEach((inLog) => {
				if (!logs[inLog.id]) {
					logs[inLog.id] = inLog;
				}
			});

			onNext(Object.values(logs));
		}, onError);
};

const siteLogsBySiteCompanyDateRangeSubscription = (
	siteCompanyID: SiteLog['site']['companyID'],
	startDate: Date,
	endDate: Date,
	onNext: (siteLogs: Record<string, SiteLog>) => void,
	onError?: (error: FirestoreError) => void,
): (() => void) => {
	return Firestore.collection(SITE_LOGS_COLLECTION)
		.where('site.companyID', '==', siteCompanyID)
		.where('out.dateTime', '>=', startDate)
		.where('out.dateTime', '<=', endDate)
		.withConverter(siteLogConverter)
		.onSnapshot(async (querySnapshot) => {
			const inLogs = await getInSiteLogsBySiteCompanyDateRange(
				siteCompanyID,
				startDate,
				endDate,
			);

			const logs = querySnapshot.docs
				.filter((doc) => doc.exists)
				.reduce<Record<string, SiteLog>>((prev, current) => {
					const data = current.data();

					prev[current.id] = data;

					return prev;
				}, {});

			inLogs.forEach((inLog) => {
				if (!logs[inLog.id]) {
					logs[inLog.id] = inLog;
				}
			});

			onNext(logs);
		}, onError);
};

const siteLogsByCompanyDateRangeSubscription = (
	companyID: string,
	startDate: Date,
	endDate: Date,
	onNext: (siteLogs: Record<string, SiteLog>) => void,
	onError?: (error: FirestoreError) => void,
): (() => void) => {
	return Firestore.collection(SITE_LOGS_COLLECTION)
		.where('company.id', '==', companyID)
		.where('out.dateTime', '>=', startDate)
		.where('out.dateTime', '<=', endDate)
		.withConverter(siteLogConverter)
		.onSnapshot(async (querySnapshot) => {
			const inLogs = await getInSiteLogsByCompanyDateRange(
				companyID,
				startDate,
				endDate,
			);

			const logs = querySnapshot.docs
				.filter((doc) => doc.exists)
				.reduce<Record<string, SiteLog>>((prev, current) => {
					const data = current.data();

					prev[current.id] = data;

					return prev;
				}, {});

			inLogs.forEach((inLog) => {
				if (!logs[inLog.id]) {
					logs[inLog.id] = inLog;
				}
			});

			onNext(logs);
		}, onError);
};

const siteLogsByContractedToDateRangeSubscription = (
	contractedToID: string,
	startDate: Date,
	endDate: Date,
	onNext: (siteLogs: Record<string, SiteLog>) => void,
	onError?: (error: FirestoreError) => void,
): (() => void) => {
	return Firestore.collection(SITE_LOGS_COLLECTION)
		.where('worker.contractedTo.id', '==', contractedToID)
		.where('out.dateTime', '>=', startDate)
		.where('out.dateTime', '<=', endDate)
		.withConverter(siteLogConverter)
		.onSnapshot(async (querySnapshot) => {
			const inLogs = await getInSiteLogsByContractedToDateRange(
				contractedToID,
				startDate,
				endDate,
			);

			const logs = querySnapshot.docs
				.filter((doc) => doc.exists)
				.reduce<Record<string, SiteLog>>((prev, current) => {
					const data = current.data();

					prev[current.id] = data;

					return prev;
				}, {});

			inLogs.forEach((inLog) => {
				if (!logs[inLog.id]) {
					logs[inLog.id] = inLog;
				}
			});

			onNext(logs);
		}, onError);
};

const siteLogsFirebaseApi = {
	getFirstWorkerSiteSignInSiteLogForPeriod,
	getSiteLogsByCompanyDateRange,
	getSiteLogsBySiteDateRange,
	getSiteLogsBySiteCompanyDateRange,
	userSiteLogsForWeekSubscription,
	siteLogsBySiteDateRangeSubscription,
	siteLogsBySiteContractedToDateRangeSubscription,
	siteLogsBySiteCompanyDateRangeSubscription,
	siteLogsByCompanyDateRangeSubscription,
	siteLogsByContractedToDateRangeSubscription,
};

export default siteLogsFirebaseApi;
